import {
  AmountListItem,
  Button,
  Chip,
  Icon,
  InfoAlert,
  RatingFormAlert,
  ReferralCodeSection,
  Section,
  SuccessAlert,
  Table,
  Tooltip,
  UnderlyingYieldBearingAssetListItem,
  WalletTokenListItem,
} from '@/components';
import {
  useNavigation,
  useRefetchYieldBearingAssets,
} from '@/components/providers';
import { useWalletsContext } from '@/components/providers/postauth/UserWalletsProvider';
import { YieldBearingAssetInterface } from '@/interfaces';
import { replaceTokensInString } from '@/utils/functionalUtils';
import { logError, logInfo } from '@/utils/logger';
import {
  TRANSACTIONS,
  YIELD_BEARING_ASSET_ACTIONS_CARD,
} from '@/utils/strings';
import { FC, useEffect, useMemo, useState } from 'react';
import { YieldBearingAssetDepositFormDataInterface } from './interfaces/YieldBearingAssetActionsInterfaces';
const visualizeElementsAndSpacing = false;
const borderClassToVisualizeElementsAndSpacing = visualizeElementsAndSpacing
  ? 'border-white border'
  : '';
const spacingConfig = {
  rootGap: 'gap-sm',
  gapBetweenTableCellAndTooltip: 'gap-sm',
  gapBetweenButtonsInFooter: 'gap-sm',
};
interface Props {
  yieldBearingAsset: YieldBearingAssetInterface | null;
  depositData: YieldBearingAssetDepositFormDataInterface;
  confirmationReceiptDoneButtonClickHandler: () => void;
}
const YieldBearingAssetInvestmentDepositConfirmationReceipt: FC<Props> = ({
  yieldBearingAsset,
  depositData,
  confirmationReceiptDoneButtonClickHandler,
}) => {
  const { navigateForward, disableBackNavigation, enableBackNavigation } =
    useNavigation();
  const { tokens, wallets } = useWalletsContext();
  const refetchYieldBearingAssets = useRefetchYieldBearingAssets();
  const [snapshotData, setSnapshotData] = useState(() => ({
    composition:
      depositData.underlyingYieldBearingAssetsAllocation?.length > 0
        ? depositData.underlyingYieldBearingAssetsAllocation
        : yieldBearingAsset?.underlyingYieldBearingAssets || [],
    token: tokens.find((t) => t.address === depositData.token?.address),
    amountInTokenCurrency: parseFloat(depositData.response?.amount || '0'),
    amountInUsd: parseFloat(depositData.response?.amountInUsd || '0'),
    networkFee: parseFloat(depositData.response?.gasCost || '0'),
    networkFeeInUsd: parseFloat(depositData.response?.gasCostInUsd || '0'),
    convenienceFee: parseFloat(depositData.response?.sliverFee || '0'),
    convenienceFeeInUsd: parseFloat(
      depositData.response?.sliverFeeInUsd || '0',
    ),
  }));
  useEffect(() => {
    disableBackNavigation();
    return () => enableBackNavigation();
  }, [disableBackNavigation, enableBackNavigation]);
  if (snapshotData.token) {
    logInfo(
      'Token Information',
      'YieldBearingAssetInvestmentDepositConfirmationReceipt',
      {
        TokenAddress: snapshotData.token.address,
        TokenName: snapshotData.token.name,
        TokenSymbol: snapshotData.token.symbol,
        BalanceFormatted: snapshotData.token.balance,
        USDValue: snapshotData.token.balanceInUsd,
        WalletAddress: snapshotData.token.walletAddress,
        Blockchain: snapshotData.token.blockchain,
      },
    );
  } else {
    logError(
      'Token not found for token address',
      'YieldBearingAssetInvestmentDepositConfirmationReceipt',
      { tokenAddress: depositData.token },
    );
  }
  const handleDone = () => {
    confirmationReceiptDoneButtonClickHandler();
    navigateForward('deposit-initial');
  };
  const replacements = useMemo(
    () => ({
      yield_bearing_asset_name: yieldBearingAsset?.name || '',
      yield_bearing_asset_ticker: yieldBearingAsset?.ticker || '',
      token_name: snapshotData.token?.name || '',
      token_symbol: snapshotData.token?.symbol || '',
      convenience_fee_percent: (
        (snapshotData.convenienceFee / snapshotData.amountInTokenCurrency) *
        100
      ).toFixed(2),
    }),
    [yieldBearingAsset, snapshotData],
  );
  const rows = useMemo(
    () => [
      {
        cells: [
          {
            mainText:
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.BOUGHT_ASSET.LABEL,
          },
          {
            mainText: (
              <div className="grid gap-sm-wholestep-dec">
                {snapshotData.composition.map((asset) => (
                  <div
                    key={asset.id}
                    className={`flex items-center 
                            ${spacingConfig.gapBetweenTableCellAndTooltip}
                            ${borderClassToVisualizeElementsAndSpacing}`}
                  >
                    <UnderlyingYieldBearingAssetListItem
                      underlyingYieldBearingAsset={asset}
                      subText={`${asset.symbol} (${(
                        asset.composition * 100
                      ).toFixed(2)}%)`}
                      align="right"
                    />
                    <Tooltip
                      trigger={<Icon name="question" intent="accent" />}
                      align="right"
                      isFullBleed={true}
                    >
                      <InfoAlert
                        title={replaceTokensInString(
                          YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                            .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.BOUGHT_ASSET
                            .TOOLTIP_TITLE,
                          replacements,
                        )}
                        description={replaceTokensInString(
                          YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                            .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.BOUGHT_ASSET
                            .TOOLTIP_DESCRIPTION,
                          replacements,
                        )}
                        intent="transparent"
                      />
                    </Tooltip>
                  </div>
                ))}
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          {
            mainText:
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.SOLD_ASSET.LABEL,
          },
          {
            mainText: (
              <div
                className={`flex items-center 
                  ${spacingConfig.gapBetweenTableCellAndTooltip}
                  ${borderClassToVisualizeElementsAndSpacing}`}
              >
                <WalletTokenListItem
                  token={snapshotData.token}
                  align="right"
                  showBalance={false}
                  showWalletAddress={false}
                  showTicker={true}
                />
                <Tooltip
                  trigger={<Icon name="question" intent="accent" />}
                  align="right"
                  isFullBleed={true}
                >
                  <InfoAlert
                    title={replaceTokensInString(
                      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                        .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.SOLD_ASSET
                        .TOOLTIP_TITLE,
                      replacements,
                    )}
                    description={replaceTokensInString(
                      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                        .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.SOLD_ASSET
                        .TOOLTIP_DESCRIPTION,
                      replacements,
                    )}
                    intent="transparent"
                  />
                </Tooltip>
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          {
            mainText:
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.AMOUNT.LABEL,
          },
          {
            mainText: (
              <div
                className={`flex items-center 
              ${spacingConfig.gapBetweenTableCellAndTooltip}
              ${borderClassToVisualizeElementsAndSpacing}`}
              >
                <AmountListItem
                  amount={snapshotData.amountInTokenCurrency}
                  subAmount={snapshotData.amountInUsd}
                  token={snapshotData.token}
                />
                <Tooltip
                  trigger={<Icon name="question" intent="accent" />}
                  align="right"
                  isFullBleed={true}
                >
                  <InfoAlert
                    title={replaceTokensInString(
                      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                        .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.AMOUNT
                        .TOOLTIP_TITLE,
                      replacements,
                    )}
                    description={replaceTokensInString(
                      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                        .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.AMOUNT
                        .TOOLTIP_DESCRIPTION,
                      replacements,
                    )}
                    intent="transparent"
                  />
                </Tooltip>
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          {
            mainText:
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.NETWORK_FEE.LABEL,
          },
          {
            mainText: (
              <div
                className={`flex items-center 
              ${spacingConfig.gapBetweenTableCellAndTooltip}
              ${borderClassToVisualizeElementsAndSpacing}`}
              >
                <AmountListItem
                  amount={snapshotData.networkFee}
                  subAmount={snapshotData.networkFeeInUsd}
                  symbol={depositData.token?.symbol || ''}
                />
                <Tooltip
                  trigger={<Icon name="question" intent="accent" />}
                  align="right"
                  isFullBleed={true}
                >
                  <InfoAlert
                    title={replaceTokensInString(
                      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                        .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.NETWORK_FEE
                        .TOOLTIP_TITLE,
                      replacements,
                    )}
                    description={replaceTokensInString(
                      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                        .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.NETWORK_FEE
                        .TOOLTIP_DESCRIPTION,
                      replacements,
                    )}
                    intent="transparent"
                  />
                </Tooltip>
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
      {
        cells: [
          {
            mainText:
              YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.CONVENIENCE_FEE.LABEL,
          },
          {
            mainText: (
              <div
                className={`flex items-center 
            ${spacingConfig.gapBetweenTableCellAndTooltip}
            ${borderClassToVisualizeElementsAndSpacing}`}
              >
                {snapshotData.convenienceFee === 0 ? (
                  <Chip
                    text={`${YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS.DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.CONVENIENCE_FEE.CHIP_LABEL_FOR_FREE}`}
                    intent="accent"
                    setChipToNoMargin={true}
                  />
                ) : (
                  <AmountListItem
                    amount={snapshotData.convenienceFee}
                    subAmount={snapshotData.convenienceFeeInUsd}
                    symbol={depositData.token?.symbol || ''}
                  />
                )}
                <Tooltip
                  trigger={<Icon name="question" intent="accent" />}
                  align="right"
                  isFullBleed={true}
                >
                  <InfoAlert
                    title={replaceTokensInString(
                      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                        .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.CONVENIENCE_FEE
                        .TOOLTIP_TITLE,
                      replacements,
                    )}
                    description={replaceTokensInString(
                      YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
                        .DEPOSIT_CONFIRMATION_RECEIPT.DETAILS.CONVENIENCE_FEE
                        .TOOLTIP_DESCRIPTION,
                      replacements,
                    )}
                    intent="transparent"
                  />
                </Tooltip>
              </div>
            ),
            align: 'right' as 'right',
          },
        ],
      },
    ],
    [snapshotData, depositData, replacements],
  );
  return (
    <div
      className={`flex flex-col flex-grow w-full h-full 
        ${spacingConfig.rootGap} 
        ${borderClassToVisualizeElementsAndSpacing}`}
    >
      <Section body={<Table rows={rows} columnWidths={[50, 50]} />} />
      <SuccessAlert
        title={TRANSACTIONS.PROCESSING_TITLE}
        description={TRANSACTIONS.PROCESSING_DESCRIPTION}
      />
      {yieldBearingAsset?.ticker && (
        <RatingFormAlert
          ticker={yieldBearingAsset.ticker}
          onRatingPosted={() => {
            logInfo('User submitted rating, refreshing yield bearing assets');
            refetchYieldBearingAssets();
          }}
        />
      )}
      <ReferralCodeSection includeApplyReferralCodeSection={false} />
      <div
        className={`w-full flex-shrink-0 flex flex-row items-center
          ${spacingConfig.gapBetweenButtonsInFooter} 
          ${borderClassToVisualizeElementsAndSpacing}`}
      >
        <Button onClick={handleDone} fullWidth>
          {
            YIELD_BEARING_ASSET_ACTIONS_CARD.SCREENS
              .DEPOSIT_CONFIRMATION_RECEIPT.DONE_BUTTON_LABEL
          }
        </Button>
      </div>
    </div>
  );
};
export default YieldBearingAssetInvestmentDepositConfirmationReceipt;
